import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Shield, Database, Lock, Users, AlertTriangle, CheckCircle } from 'lucide-react';

export function PermissionDocs() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Shield className="w-8 h-8 text-primary" />
          دليل نظام الصلاحيات والأمان
        </h1>
        <p className="text-muted-foreground mt-2">
          الدليل الشامل لنظام RBAC وRLS وإدارة الصلاحيات
        </p>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview">نظرة عامة</TabsTrigger>
          <TabsTrigger value="rbac">RBAC</TabsTrigger>
          <TabsTrigger value="rls">RLS</TabsTrigger>
          <TabsTrigger value="testing">الاختبار</TabsTrigger>
          <TabsTrigger value="troubleshooting">استكشاف الأخطاء</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>نظرة عامة على نظام الأمان</CardTitle>
              <CardDescription>
                فهم البنية الأمنية الشاملة للنظام
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  طبقات الأمان
                </h3>
                <ol className="list-decimal list-inside space-y-2 text-sm">
                  <li><strong>Authentication:</strong> التحقق من هوية المستخدم عبر Supabase Auth</li>
                  <li><strong>RBAC (Role-Based Access Control):</strong> التحكم بناءً على الأدوار</li>
                  <li><strong>RLS (Row Level Security):</strong> أمان على مستوى الصف في قاعدة البيانات</li>
                  <li><strong>Frontend Guards:</strong> حماية المسارات في الواجهة</li>
                  <li><strong>Audit Logging:</strong> تسجيل جميع العمليات الحساسة</li>
                </ol>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2">مبدأ Least Privilege</h4>
                <p className="text-sm text-blue-800">
                  كل مستخدم يحصل فقط على الحد الأدنى من الصلاحيات المطلوبة لأداء مهامه.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rbac" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                نظام RBAC - التحكم بناءً على الأدوار
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">الأدوار المتاحة</h3>
                <div className="space-y-2 text-sm">
                  <div className="p-3 border rounded-lg">
                    <strong className="text-red-600">super_admin:</strong> صلاحيات كاملة لكل شيء
                  </div>
                  <div className="p-3 border rounded-lg">
                    <strong className="text-orange-600">admin:</strong> إدارة عامة للنظام
                  </div>
                  <div className="p-3 border rounded-lg">
                    <strong className="text-yellow-600">manager:</strong> إدارة قسم معين
                  </div>
                  <div className="p-3 border rounded-lg">
                    <strong className="text-green-600">sales_manager:</strong> إدارة المبيعات
                  </div>
                  <div className="p-3 border rounded-lg">
                    <strong className="text-green-500">sales:</strong> موظف مبيعات
                  </div>
                  <div className="p-3 border rounded-lg">
                    <strong className="text-blue-600">hr_manager:</strong> إدارة الموارد البشرية
                  </div>
                  <div className="p-3 border rounded-lg">
                    <strong className="text-blue-500">hr:</strong> موظف موارد بشرية
                  </div>
                  <div className="p-3 border rounded-lg">
                    <strong className="text-purple-600">accountant:</strong> محاسب
                  </div>
                  <div className="p-3 border rounded-lg">
                    <strong className="text-pink-600">receptionist:</strong> موظف استقبال
                  </div>
                  <div className="p-3 border rounded-lg">
                    <strong className="text-gray-600">user:</strong> مستخدم عادي
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-semibold mb-2">كيفية تعيين الأدوار</h3>
                <ol className="list-decimal list-inside space-y-1 text-sm">
                  <li>انتقل إلى صفحة الموظفين</li>
                  <li>افتح نافذة تعديل الموظف</li>
                  <li>انتقل إلى تبويب "أدوار النظام (RBAC)"</li>
                  <li>اختر الأدوار المناسبة</li>
                  <li>احفظ التغييرات</li>
                </ol>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rls" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="w-5 h-5" />
                نظام RLS - أمان مستوى الصف
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">ما هو RLS؟</h3>
                <p className="text-sm text-muted-foreground">
                  Row Level Security هو نظام أمان في PostgreSQL يسمح بالتحكم في الوصول على مستوى الصف في الجداول.
                  هذا يعني أن كل مستخدم يرى فقط البيانات المصرح له بها.
                </p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">أمثلة على Policies</h3>
                <div className="space-y-3">
                  <div className="p-3 border rounded-lg bg-green-50">
                    <strong className="text-green-800">✓ Clients Policy:</strong>
                    <p className="text-sm text-green-700 mt-1">
                      موظف المبيعات يرى فقط العملاء الذين أضافهم أو يتابعهم
                    </p>
                  </div>
                  
                  <div className="p-3 border rounded-lg bg-blue-50">
                    <strong className="text-blue-800">✓ Bookings Policy:</strong>
                    <p className="text-sm text-blue-700 mt-1">
                      الموظف يرى فقط الحجوزات التي أنشأها، والمدراء يرون كل شيء
                    </p>
                  </div>
                  
                  <div className="p-3 border rounded-lg bg-purple-50">
                    <strong className="text-purple-800">✓ Employees Policy:</strong>
                    <p className="text-sm text-purple-700 mt-1">
                      كل موظف يرى معلوماته الخاصة، HR يرون الجميع
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="testing" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                اختبار نظام الصلاحيات
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">خطوات الاختبار الأساسية</h3>
                <ol className="list-decimal list-inside space-y-2 text-sm">
                  <li>أنشئ مستخدم تجريبي بدور محدد</li>
                  <li>سجل دخول بهذا المستخدم</li>
                  <li>حاول الوصول إلى صفحات مختلفة</li>
                  <li>تأكد من ظهور الصفحات المصرح بها فقط</li>
                  <li>حاول إجراء عمليات غير مصرح بها</li>
                  <li>تأكد من رفض الوصول</li>
                </ol>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h4 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  مهم
                </h4>
                <p className="text-sm text-amber-800">
                  لا تختبر الصلاحيات باستخدام حساب المدير الرئيسي - استخدم حسابات تجريبية
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="troubleshooting" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                استكشاف المشاكل الشائعة
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">المشكلة: المستخدم لا يرى البيانات</h3>
                <div className="space-y-2 text-sm">
                  <p><strong>الحل 1:</strong> تحقق من RLS Policies في Supabase Dashboard</p>
                  <p><strong>الحل 2:</strong> تأكد من أن المستخدم لديه الدور الصحيح</p>
                  <p><strong>الحل 3:</strong> تحقق من أن user_id مطابق في الجداول</p>
                </div>
              </div>

              <div>
                <h3 className="font-semibold mb-2">المشكلة: "Access Denied"</h3>
                <div className="space-y-2 text-sm">
                  <p><strong>الحل 1:</strong> تحقق من الأدوار المعينة للمستخدم</p>
                  <p><strong>الحل 2:</strong> راجع requiredRoles في RoleProtectedRoute</p>
                  <p><strong>الحل 3:</strong> تأكد من تطبيق Migration</p>
                </div>
              </div>

              <div>
                <h3 className="font-semibold mb-2">المشكلة: Infinite Recursion in RLS</h3>
                <div className="space-y-2 text-sm">
                  <p><strong>الحل:</strong> استخدم SECURITY DEFINER functions بدلاً من الاستعلام المباشر في Policies</p>
                  <code className="block bg-gray-100 p-2 rounded text-xs mt-2">
                    CREATE FUNCTION get_current_employee_id_safe() ...<br/>
                    SECURITY DEFINER SET search_path = public
                  </code>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
